'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

import { CompetitionStatusEnum } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { SpecialButton } from '@flipster/react/design-system/special/button';
import { css } from '@flipster/styled-system/css';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';
import webviewHandler from '@flipster/utils/webview-handler';

import { registerCompetitionBunja } from '../action';
import { dailyCheckInActionBunja } from '../content/daily-check-in/action';
import { dailyCheckInBunja } from '../content/daily-check-in/state';
import { CompetitionIdContext, competitionApiBunja, competitionBunja, dailyCheckInApiBunja } from '../state';

export const RegisterButton = ({ buttonLocation }: { buttonLocation: string }) => {
  const id = useContext(CompetitionIdContext);
  const router = useRouter();
  const { registerCompetition } = useBunja(registerCompetitionBunja);
  const { isRegisteredAtom } = useBunja(competitionBunja);
  const { competitionStatusAtom } = useBunja(competitionApiBunja);
  const isRegistered = useAtomValue(isRegisteredAtom);
  const competitionStatus = useAtomValue(competitionStatusAtom);

  const isDisabled = competitionStatus === CompetitionStatusEnum.Upcoming && isRegistered;

  if (competitionStatus === CompetitionStatusEnum.Finished) return null;

  if (competitionStatus === CompetitionStatusEnum.Ongoing && isRegistered)
    return (
      <SpecialButton
        label={<FormattedMessage id='Competition.r251215.Trade_now' />}
        onClick={() => {
          segment.track(CompetitionsEvent.CompetitionTradingTradeClick, {
            competition_id: id,
            button_location: buttonLocation,
          });
          webviewHandler.postMessage({ action: 'link', params: { page: 'trade/latest' } }, () =>
            router.push('/trade/perpetual/latest'),
          );
        }}
        css={buttonCss}
      />
    );

  return (
    <SpecialButton
      label={
        isRegistered ? (
          <FormattedMessage id='Common.r251028.Registered' />
        ) : (
          <FormattedMessage id='Autogen_common.r251126.s_Register_Now' />
        )
      }
      hierarchy='primary'
      disabled={isDisabled}
      onClick={async () => {
        segment.track(CompetitionsEvent.CompetitionTradingRegisterClick, {
          competition_id: id,
          button_location: buttonLocation,
        });
        await registerCompetition();
      }}
      css={buttonCss}
    />
  );
};

export const DailyCheckInButton = ({ buttonLocation }: { buttonLocation: string }) => {
  const id = useContext(CompetitionIdContext);
  const { registerCompetition } = useBunja(registerCompetitionBunja);
  const { isRegisteredAtom } = useBunja(competitionBunja);
  const { dailyCheckInEventStatusAtom } = useBunja(dailyCheckInApiBunja);
  const { dailyCheckInMutationAtom } = useBunja(dailyCheckInActionBunja);
  const { dailyCheckInSuccessAtom } = useBunja(dailyCheckInBunja);
  const isRegistered = useAtomValue(isRegisteredAtom);
  const dailyCheckInEventStatus = useAtomValue(dailyCheckInEventStatusAtom);
  const { mutateAsync: checkInAsync, isPending: isCheckInPending } = useAtomValue(dailyCheckInMutationAtom);
  const { isCheckedInToday } = useAtomValue(dailyCheckInSuccessAtom);

  if (dailyCheckInEventStatus === CompetitionStatusEnum.Finished) return null;

  if (dailyCheckInEventStatus === CompetitionStatusEnum.Ongoing && isRegistered)
    return (
      <SpecialButton
        label={<FormattedMessage id='Competition.r251215.Checkin' />}
        onClick={async () => {
          if (isCheckInPending) return;
          await checkInAsync();
        }}
        disabled={isCheckInPending || isCheckedInToday}
        css={buttonCss}
      />
    );

  return (
    <SpecialButton
      label={
        isRegistered ? (
          <FormattedMessage id='Common.r251028.Registered' />
        ) : (
          <FormattedMessage id='Autogen_common.r251126.s_Register_Now' />
        )
      }
      hierarchy='primary'
      disabled={isRegistered}
      onClick={async () => {
        segment.track(CompetitionsEvent.CompetitionTradingRegisterClick, {
          competition_id: id,
          button_location: buttonLocation,
        });
        await registerCompetition();
      }}
      css={buttonCss}
    />
  );
};

const buttonCss = css.raw({ minW: { base: 212, tablet: 260 }, maxW: '100%' });
